
import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
/**
 * Helper function to process timestamps from backend
 * @param {Object} data - Data object from backend
 * @returns {Object} Data with timestamps in expected format
 */
const processTimestamps = (data) => {
  return {
    ...data,
    // Backend returns ISO strings, ensure they're in the right format
    timestamp: data.timestamp ? new Date(data.timestamp).toISOString() : null,
    createdAt: data.createdAt ? new Date(data.createdAt).toISOString() : null
  };
};

/**
 * Secure Clock Event Service
 * Uses backend API endpoints instead of direct Firestore access
 */

/**
 * Get clock events for a user or facility
 * @param {string} facilityId - Facility ID
 * @param {Object} filters - Filters (employeeId, date, eventType)
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @returns {Promise<Object>} Clock events with pagination
 */
export const getClockEvents = async (facilityId, filters = {}, page = 1, limit = 50) => {
  try {
    console.log('Fetching clock events for facility:', facilityId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    if (facilityId) params.append('facilityId', facilityId);
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/admin/clock-events?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      clockEvents: response.data.clockEvents.map(processTimestamps),
      pagination: response.data.pagination
    };
  } catch (error) {
    console.error('Error fetching clock events:', error);
    throw error;
  }
};

/**
 * Create a new clock event (clock in/out)
 * @param {Object} eventData - Clock event data
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Created clock event
 */
export const createClockEvent = async (eventData, facilityId) => {
  try {
    console.log('Creating clock event:', eventData.eventType);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/admin/clock-events`,
      {
        ...eventData,
        facilityId
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return processTimestamps(response.data);
  } catch (error) {
    console.error('Error creating clock event:', error);
    throw error;
  }
};

/**
 * Clock in for the current user
 * @param {string} facilityId - Facility ID
 * @param {Object} additionalData - Additional data (location, notes, etc.)
 * @returns {Promise<Object>} Clock in result
 */
export const clockIn = async (facilityId, additionalData = {}) => {
  try {
    console.log('Clocking in for facility:', facilityId);
    
    const eventData = {
      eventType: 'clock_in',
      timestamp: new Date().toISOString(),
      ...additionalData
    };
    
    return await createClockEvent(eventData, facilityId);
  } catch (error) {
    console.error('Error clocking in:', error);
    throw error;
  }
};

/**
 * Clock out for the current user
 * @param {string} facilityId - Facility ID
 * @param {Object} additionalData - Additional data (location, notes, etc.)
 * @returns {Promise<Object>} Clock out result
 */
export const clockOut = async (facilityId, additionalData = {}) => {
  try {
    console.log('Clocking out for facility:', facilityId);
    
    const eventData = {
      eventType: 'clock_out',
      timestamp: new Date().toISOString(),
      ...additionalData
    };
    
    return await createClockEvent(eventData, facilityId);
  } catch (error) {
    console.error('Error clocking out:', error);
    throw error;
  }
};

/**
 * Get current clock status for a user
 * @param {string} facilityId - Facility ID
 * @param {string} employeeId - Employee ID (optional, defaults to current user)
 * @returns {Promise<Object>} Current clock status
 */
export const getCurrentClockStatus = async (facilityId, employeeId = null) => {
  try {
    console.log('Getting current clock status');
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    if (employeeId) params.append('employeeId', employeeId);
    
    const response = await axios.get(
      `${API_BASE}/admin/clock-events/current-status?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.status;
  } catch (error) {
    console.error('Error getting current clock status:', error);
    throw error;
  }
};

/**
 * Get clock events for today for a specific user
 * @param {string} facilityId - Facility ID
 * @param {string} employeeId - Employee ID (optional, defaults to current user)
 * @returns {Promise<Array>} Today's clock events
 */
export const getTodayClockEvents = async (facilityId, employeeId = null) => {
  try {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
    
    const filters = { date: today };
    if (employeeId) filters.employeeId = employeeId;
    
    const result = await getClockEvents(facilityId, filters);
    return result.clockEvents;
  } catch (error) {
    console.error('Error getting today clock events:', error);
    throw error;
  }
};

/**
 * Get clock events for a date range
 * @param {string} facilityId - Facility ID
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string} employeeId - Employee ID (optional)
 * @returns {Promise<Array>} Clock events in date range
 */
export const getClockEventsInDateRange = async (facilityId, startDate, endDate, employeeId = null) => {
  try {
    console.log('Getting clock events for date range:', startDate, 'to', endDate);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    if (employeeId) params.append('employeeId', employeeId);
    
    const response = await axios.get(
      `${API_BASE}/admin/clock-events/date-range?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.clockEvents.map(processTimestamps);
  } catch (error) {
    console.error('Error getting clock events in date range:', error);
    throw error;
  }
};

/**
 * Calculate total hours worked for a user in a date range
 * @param {string} facilityId - Facility ID
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string} employeeId - Employee ID (optional, defaults to current user)
 * @returns {Promise<Object>} Hours worked data
 */
export const calculateHoursWorked = async (facilityId, startDate, endDate, employeeId = null) => {
  try {
    console.log('Calculating hours worked for date range:', startDate, 'to', endDate);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    if (employeeId) params.append('employeeId', employeeId);
    
    const response = await axios.get(
      `${API_BASE}/admin/clock-events/hours-worked?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.hoursWorked;
  } catch (error) {
    console.error('Error calculating hours worked:', error);
    throw error;
  }
};

/**
 * Get timesheet for a user
 * @param {string} facilityId - Facility ID
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @param {string} employeeId - Employee ID (optional, defaults to current user)
 * @returns {Promise<Object>} Timesheet data
 */
export const getTimesheet = async (facilityId, startDate, endDate, employeeId = null) => {
  try {
    console.log('Getting timesheet for date range:', startDate, 'to', endDate);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('facilityId', facilityId);
    params.append('startDate', startDate);
    params.append('endDate', endDate);
    if (employeeId) params.append('employeeId', employeeId);
    
    const response = await axios.get(
      `${API_BASE}/admin/clock-events/timesheet?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.timesheet;
  } catch (error) {
    console.error('Error getting timesheet:', error);
    throw error;
  }
};

/**
 * Admin function: Get clock events for all employees in a facility
 * @param {string} facilityId - Facility ID
 * @param {string} date - Date (YYYY-MM-DD)
 * @returns {Promise<Array>} All clock events for the facility on the date
 */
export const getAllEmployeeClockEvents = async (facilityId, date) => {
  try {
    console.log('Getting all employee clock events for facility:', facilityId, 'on date:', date);
    const result = await getClockEvents(facilityId, { date });
    return result.clockEvents;
  } catch (error) {
    console.error('Error getting all employee clock events:', error);
    throw error;
  }
};

/**
 * Update a clock event (admin only)
 * @param {string} eventId - Clock event ID
 * @param {Object} updateData - Data to update
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Update result
 */
export const updateClockEvent = async (eventId, updateData, facilityId) => {
  try {
    console.log('Updating clock event:', eventId);
    const token = await getAuthToken();
    
    const response = await axios.put(
      `${API_BASE}/admin/clock-events/${eventId}`,
      {
        ...updateData,
        facilityId
      },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error updating clock event:', error);
    throw error;
  }
};

/**
 * Delete a clock event (admin only)
 * @param {string} eventId - Clock event ID
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Deletion result
 */
export const deleteClockEvent = async (eventId, facilityId) => {
  try {
    console.log('Deleting clock event:', eventId);
    const token = await getAuthToken();
    
    const response = await axios.delete(
      `${API_BASE}/admin/clock-events/${eventId}?facilityId=${facilityId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error deleting clock event:', error);
    throw error;
  }
};

/**
 * Check if a user needs to clock in (for auto clock-in features)
 * @param {string} employeeId - Employee ID
 * @param {string} facilityId - Facility ID
 * @returns {Promise<Object>} Clock-in requirement status
 */
export const checkClockInRequired = async (employeeId, facilityId) => {
  try {
    console.log('Checking clock-in requirement for employee:', employeeId);
    
    // Check current clock status
    const currentStatus = await getCurrentClockStatus(facilityId, employeeId);
    
    // If already clocked in, no action needed
    if (currentStatus.status === 'clocked_in') {
      return {
        required: false,
        reason: 'already_clocked_in',
        currentStatus
      };
    }
    
    // Get today's events to check if clock-in is needed
    const todayEvents = await getTodayClockEvents(facilityId, employeeId);
    
    // If no events today and it's work hours, clock-in is required
    const now = new Date();
    const hour = now.getHours();
    const isWorkHours = hour >= 6 && hour <= 22; // Basic work hours check
    
    if (todayEvents.length === 0 && isWorkHours) {
      return {
        required: true,
        reason: 'no_clock_in_today',
        currentStatus
      };
    }
    
    return {
      required: false,
      reason: 'not_required',
      currentStatus
    };
  } catch (error) {
    console.error('Error checking clock-in requirement:', error);
    return {
      required: false,
      reason: 'error',
      error: error.message
    };
  }
};

/**
 * ClockEventService object - for components that import the service as an object
 */
export const ClockEventService = {
  getClockEvents,
  createClockEvent,
  clockIn,
  clockOut,
  getCurrentClockStatus,
  getTodayClockEvents,
  getClockEventsInDateRange,
  calculateHoursWorked,
  getTimesheet,
  getAllEmployeeClockEvents,
  updateClockEvent,
  deleteClockEvent,
  checkClockInRequired
};